import { Badge } from "@repo/ui/badge";
import { Button } from "@repo/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@repo/ui/card";
import { Checkbox } from "@repo/ui/checkbox";
import { Input } from "@repo/ui/input";
import { Label } from "@repo/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@repo/ui/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, MapPinned, Play } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  fetchLatestSuburbSnapshots,
  fetchSuburbProfileAdapters,
  triggerSuburbCollection,
  type LatestSuburbSnapshot,
  type SuburbProfileAdapter,
} from "@/lib/api";

interface SuburbLookupRow {
  id: string;
  suburb_name: string | null;
  locality: string | null;
  state: string;
  postcode: string;
}

function formatCurrency(value: number | null): string {
  if (value === null || value === undefined) return "—";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPercent(value: number | null): string {
  if (value === null || value === undefined) return "—";
  return `${value > 0 ? "+" : ""}${value.toFixed(1)}%`;
}

function formatMonth(value: string): string {
  const [y, m] = value.split("-");
  if (!y || !m) return value;
  const date = new Date(Number(y), Number(m) - 1, 1);
  return date.toLocaleString("en-AU", { month: "short", year: "numeric" });
}

const SuburbInformation = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [lookupQuery, setLookupQuery] = useState("");
  const [selectedSuburbId, setSelectedSuburbId] = useState<string | null>(null);
  const [selectedAdapterSlugs, setSelectedAdapterSlugs] = useState<string[]>([]);

  const { data: snapshots, isLoading, error } = useQuery<LatestSuburbSnapshot[]>({
    queryKey: ["latest-suburb-snapshots"],
    queryFn: fetchLatestSuburbSnapshots,
  });

  const { data: adapters } = useQuery<SuburbProfileAdapter[]>({
    queryKey: ["suburb-profile-adapters"],
    queryFn: fetchSuburbProfileAdapters,
  });

  // Live suburb search — hits the suburbs table directly. Debounce via
  // React Query's natural caching; the user just types and we re-query.
  const trimmedLookup = lookupQuery.trim();
  const { data: lookupResults, isFetching: lookupFetching } = useQuery<SuburbLookupRow[]>({
    queryKey: ["suburb-lookup", trimmedLookup],
    enabled: trimmedLookup.length >= 2,
    queryFn: async () => {
      const q = trimmedLookup;
      const { data, error } = await supabase
        .from("suburbs")
        .select("id, suburb_name, locality, state, postcode")
        .or(
          `suburb_name.ilike.%${q}%,locality.ilike.%${q}%,postcode.ilike.${q}%`,
        )
        .limit(15);
      if (error) throw error;
      return (data ?? []) as unknown as SuburbLookupRow[];
    },
  });

  const filtered = useMemo(() => {
    if (!snapshots) return [];
    const q = search.trim().toLowerCase();
    if (!q) return snapshots;
    return snapshots.filter(
      (s) =>
        s.suburb_name.toLowerCase().includes(q) ||
        s.postcode.includes(q) ||
        s.state.toLowerCase().includes(q) ||
        (s.adapter_name?.toLowerCase().includes(q) ?? false),
    );
  }, [snapshots, search]);

  const selectedSuburb = useMemo(
    () => lookupResults?.find((r) => r.id === selectedSuburbId) ?? null,
    [lookupResults, selectedSuburbId],
  );

  const toggleAdapter = (slug: string) => {
    setSelectedAdapterSlugs((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );
  };

  const triggerMutation = useMutation({
    mutationFn: async () => {
      if (!selectedSuburbId) throw new Error("Pick a suburb first.");
      if (selectedAdapterSlugs.length === 0)
        throw new Error("Pick at least one adapter.");
      return triggerSuburbCollection({
        suburbIds: [selectedSuburbId],
        adapterSlugs: selectedAdapterSlugs,
        runType: "manual",
        force: true,
      });
    },
    onSuccess: (result) => {
      const perAdapter = result.runs ?? [];
      const summary = perAdapter.length
        ? perAdapter
            .map((r) => `${r.adapterSlug}: ${r.done}/${r.suburbsTotal}`)
            .join(" · ")
        : `${result.done}/${result.suburbsTotal}`;
      toast({
        title: "Collection complete",
        description: summary,
      });
      queryClient.invalidateQueries({ queryKey: ["latest-suburb-snapshots"] });
    },
    onError: (err) => {
      toast({
        title: "Collection failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    },
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <MapPinned className="w-6 h-6" />
          Suburb Information
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Latest snapshot per suburb × adapter. Look up any suburb by name or
          postcode and trigger one or more adapters for the current month.
        </p>
      </div>

      {/* === Lookup + run === */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Run adapters for a suburb</CardTitle>
          <CardDescription>
            We can only crawl the current month — re-running over time builds
            month-by-month history.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] items-end">
            <div className="space-y-1">
              <Label htmlFor="suburb-lookup">Suburb name or postcode</Label>
              <Input
                id="suburb-lookup"
                type="search"
                placeholder="e.g. McKinnon or 3204"
                value={lookupQuery}
                onChange={(e) => {
                  setLookupQuery(e.target.value);
                  setSelectedSuburbId(null);
                }}
              />
              {trimmedLookup.length >= 2 ? (
                <div className="rounded-md border bg-background max-h-48 overflow-y-auto text-sm">
                  {lookupFetching ? (
                    <div className="flex items-center gap-2 p-2 text-muted-foreground">
                      <Loader2 className="h-3 w-3 animate-spin" /> Searching…
                    </div>
                  ) : lookupResults && lookupResults.length > 0 ? (
                    lookupResults.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setSelectedSuburbId(s.id)}
                        className={`w-full text-left px-2 py-1 hover:bg-muted ${
                          selectedSuburbId === s.id ? "bg-muted" : ""
                        }`}
                      >
                        <span className="font-medium">
                          {s.suburb_name ?? s.locality}
                        </span>{" "}
                        <span className="text-muted-foreground">
                          · {s.state} {s.postcode}
                        </span>
                      </button>
                    ))
                  ) : (
                    <p className="p-2 text-muted-foreground">No matches.</p>
                  )}
                </div>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label>Adapters</Label>
              <div className="space-y-1.5">
                {(adapters ?? []).map((adapter) => (
                  <div key={adapter.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`adapter-${adapter.slug}`}
                      checked={selectedAdapterSlugs.includes(adapter.slug)}
                      onCheckedChange={() => toggleAdapter(adapter.slug)}
                    />
                    <Label
                      htmlFor={`adapter-${adapter.slug}`}
                      className="font-normal cursor-pointer"
                    >
                      {adapter.name}{" "}
                      <span className="text-muted-foreground text-xs">
                        ({adapter.slug})
                      </span>
                    </Label>
                  </div>
                ))}
                {(adapters?.length ?? 0) === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    No suburb adapters registered.
                  </p>
                ) : null}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              type="button"
              onClick={() => triggerMutation.mutate()}
              disabled={
                triggerMutation.isPending ||
                !selectedSuburbId ||
                selectedAdapterSlugs.length === 0
              }
            >
              {triggerMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Play className="mr-2 h-4 w-4" />
              )}
              Run {selectedAdapterSlugs.length || 0} adapter(s) for current month
            </Button>
            {selectedSuburb ? (
              <p className="text-sm text-muted-foreground">
                Target:{" "}
                <span className="font-medium text-foreground">
                  {selectedSuburb.suburb_name ?? selectedSuburb.locality}
                </span>
                , {selectedSuburb.state} {selectedSuburb.postcode}
              </p>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {/* === Snapshot list === */}
      <Card>
        <CardHeader>
          <CardTitle>Latest snapshots per suburb × adapter</CardTitle>
          <CardDescription>
            Each row is the most recent monthly capture from a given adapter.
            Click a suburb to drill into its full month-by-month history across
            all adapters.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input
            type="search"
            placeholder="Filter by suburb, postcode, state, or adapter…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : error ? (
            <p className="text-sm text-destructive py-8">
              Failed to load snapshots: {(error as Error).message}
            </p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8">
              {search
                ? "No suburbs match that filter."
                : "No snapshots collected yet. Use the runner above."}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Suburb</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Postcode</TableHead>
                  <TableHead>Adapter</TableHead>
                  <TableHead>Month</TableHead>
                  <TableHead>Median house</TableHead>
                  <TableHead>Median unit</TableHead>
                  <TableHead>Annual growth</TableHead>
                  <TableHead>Bands</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((row) => (
                  <TableRow key={row.id} className="hover:bg-muted/50">
                    <TableCell>
                      <Link
                        to={`/dashboard/suburbs/${row.suburb_id}`}
                        className="font-medium hover:underline"
                      >
                        {row.suburb_name}
                      </Link>
                    </TableCell>
                    <TableCell>{row.state}</TableCell>
                    <TableCell>{row.postcode}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="font-mono text-xs">
                        {row.adapter_slug ?? "?"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {formatMonth(row.snapshot_month)}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatCurrency(row.median_house_price)}</TableCell>
                    <TableCell>{formatCurrency(row.median_unit_price)}</TableCell>
                    <TableCell
                      className={
                        row.annual_growth_pct === null
                          ? ""
                          : row.annual_growth_pct >= 0
                            ? "text-success"
                            : "text-destructive"
                      }
                    >
                      {formatPercent(row.annual_growth_pct)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {row.bedroom_breakdowns?.length
                        ? `${row.bedroom_breakdowns.length}`
                        : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SuburbInformation;
